import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { saveUser } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Connexion() {
  const navigate = useNavigate();
  const [contact, setContact] = useState("");
  const [password, setPassword] = useState("");
  const [code2fa, setCode2fa] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/connexion`, { contact, password: password || null, code_2fa: code2fa || null });
      saveUser(res.data);
      toast.success(`Bon retour, ${res.data.firstname} !`);
      navigate(res.data.role === "admin" ? "/admin" : "/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "OTP_REQUIRED") {
        sessionStorage.setItem("fodindin_otp", JSON.stringify({ profile_id: detail.profile_id, next: "/connexion" }));
        toast.info(detail.message);
        navigate("/verification");
      } else if (err.response?.status === 403 && detail?.code === "TWO_FA_REQUIRED") {
        setNeeds2fa(true);
        toast.info(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur de connexion.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-md mx-auto px-4 pt-36 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Connexion</span>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-stone-900 mt-3">Bon retour !</h1>
          <p className="text-stone-600 mt-3 text-sm leading-relaxed">
            Entrez le téléphone ou l'email renseigné à l'inscription pour retrouver votre compte — sans mot de passe.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="connexion-form">
            <input
              data-testid="connexion-contact"
              className="w-full bg-white border border-[#E5DFD5] rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/50"
              placeholder="Ex : +225 07 00 00 00 00"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
            />
            <input
              data-testid="connexion-password"
              type="password"
              className="w-full bg-white border border-[#E5DFD5] rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/50"
              placeholder="Mot de passe (comptes administrateurs uniquement)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {needs2fa && (
              <input
                data-testid="connexion-2fa"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                className="w-full bg-white border-2 border-[#0A5C36] rounded-xl px-4 py-3.5 text-sm tracking-[0.3em] text-center font-mono focus:outline-none focus:ring-2 focus:ring-[#0A5C36]/40"
                placeholder="Code 6 chiffres (Google Authenticator)"
                value={code2fa}
                onChange={(e) => setCode2fa(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="connexion-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E05638] hover:bg-[#C84428] disabled:opacity-50 text-white font-semibold py-3.5 rounded-full transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Se connecter
            </button>
          </form>
          <p className="text-sm text-stone-500 mt-6 text-center">
            Pas encore de compte ?{" "}
            <Link to="/inscription" data-testid="connexion-vers-inscription" className="text-[#E05638] font-semibold hover:underline">
              Inscrivez-vous gratuitement
            </Link>
          </p>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
